import { useMemo } from "react"

import { CheckOutlined } from "@ant-design/icons"
import { Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../../../Components/Button"
import { RoutesPaths } from "../../../../../../constants/user-routes"
import Icon from "../../../../../../Icon"
import { paymentProcessorTitle } from "../../../../../../Utils/utils"
import {
  selectActiveBuildingBlocks,
  selectActiveProcessor,
  selectConnectedProcessors
} from "../../redux/configure.slice"
import { commitSection } from "../../redux/configure.thunks"
import {
  getSectionKeys,
  getSectionLabel,
  getSectionStep,
  hasPreviewStep,
  isExperimentalContentStep,
  stepSections
} from "../../schema/sections"
import { isOptionalStep, optionalStepStarted, stepCount } from "../../utils/sectionProgress"
import { firstIncompleteStep, isStepComplete } from "../../utils/sectionValidation"

import configureStyles from "../../configure.module.less"
import styles from "./leftRail.module.less"

const { Text } = Typography

// Readable processor name for the "Steps for X" label (falls back to a
// capitalized slug for anything not in the shared title map).
const procTitle = (slug) =>
  paymentProcessorTitle?.[slug] || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : slug)

// One item in the journey rail. Renders a step badge (circle with a number,
// turns into a green checkmark when complete) + the section title + the X/Y
// answered counter.
const SectionItem = ({
  stepNumber,
  label,
  count,
  optional,
  active,
  complete,
  locked,
  lockReason,
  onClick,
  t
}) => {
  const item = (
    <button
      type="button"
      className={`${styles.sectionItem} ${active ? styles.active : ""} ${complete ? styles.isComplete : ""} ${locked ? styles.isLocked : ""}`}
      aria-current={active ? "step" : undefined}
      disabled={locked}
      onClick={onClick}
    >
      {/* The review entry (Preview & Confirm) carries no step number: it is where the
          merchant checks what the numbered steps produced, so numbering it would make the
          rail's total disagree with the per-processor "N of M" counter. */}
      <span className={styles.stepBadge}>
        {complete ? <CheckOutlined /> : (stepNumber ?? <Icon type="eye" />)}
      </span>
      <span className={styles.sectionItemMeta}>
        <span className={styles.sectionItemStep}>
          {stepNumber ? t("Step _n_", { n: stepNumber }) : t("Review")}
        </span>
        <span className={styles.sectionItemLabel}>{t(label)}</span>
      </span>
      <span className={styles.sectionItemCount}>
        {complete ? (
          <Text type="secondary">{t("Done")}</Text>
        ) : optional ? (
          /* Everything in this step is optional and nothing is filled in yet: say so
             rather than claiming it is finished. */
          <Text type="secondary">{t("Optional")}</Text>
        ) : count ? (
          <Text type="secondary">
            {count.answered}/{count.total}
          </Text>
        ) : null}
      </span>
    </button>
  )
  // A locked step explains itself rather than silently doing nothing on click.
  return locked ? (
    <Tooltip title={lockReason}>
      <span>{item}</span>
    </Tooltip>
  ) : (
    item
  )
}

const LeftRail = ({ currentSection, profileFinished, onOpenPreview }) => {
  const dispatch = useDispatch()
  const buildingBlocks = useSelector(selectActiveBuildingBlocks)
  const connectedProcessors = useSelector(selectConnectedProcessors)
  const activeProcessor = useSelector(selectActiveProcessor)
  const { t } = useTranslation()
  const navigate = useNavigate()

  const sectionKeys = getSectionKeys()
  const perSectionCount = useMemo(
    () => Object.fromEntries(sectionKeys.map((k) => [k, stepCount(k, buildingBlocks)])),
    // sectionKeys is derived from the (stable within a render) experimental flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildingBlocks]
  )

  // Navigate to a step (or the Summary), committing the section we're leaving first so a
  // rail jump never drops in-progress edits on refresh.
  const goTo = (target) => {
    if (sectionKeys.includes(currentSection) && currentSection !== target) {
      // An experimental content step spans several buckets; commit each so a rail jump
      // never drops in-progress edits (a plain commit of the phantom step key wouldn't).
      if (isExperimentalContentStep(currentSection)) {
        stepSections(currentSection).forEach((s) => dispatch(commitSection({ section: s })))
      } else {
        dispatch(commitSection({ section: currentSection }))
      }
    }
    navigate(RoutesPaths.recover.configure_section(target))
  }

  // Forward-jump gate: the first step with an empty/invalid required field bounds how
  // far the rail can jump. Everything up to and including it stays clickable so the
  // merchant can go back and fix things; later steps are locked. Only the Preview &
  // Confirm flow enforces this; every other flow keeps free rail navigation.
  const firstIncomplete = useMemo(
    () => (hasPreviewStep() ? firstIncompleteStep(buildingBlocks) : null),
    [buildingBlocks]
  )
  const everythingComplete = firstIncomplete == null
  const blockedFrom = firstIncomplete == null ? null : sectionKeys.indexOf(firstIncomplete)

  // Preview needs Business Profile and Customer Experience done, otherwise the evidence
  // has nothing meaningful to render. Gating on the storage buckets instead of the steps
  // would let a merchant preview with Customer Experience untouched, since its fields are
  // spread across three buckets. "Done" = the step's required fields are valid, not every
  // optional one.
  const canPreview = useMemo(
    () =>
      isStepComplete("business_profile", buildingBlocks) &&
      isStepComplete("customer_experience", buildingBlocks),
    [buildingBlocks]
  )

  return (
    <aside className={styles.configureRail}>
      <div className={styles.configureRailTop}>
        {/*  Rail heading: gives the step journey a title + context, divider below.  */}
        <div className={styles.configureRailHead}>
          <span className={styles.configureRailTitle}>
            {profileFinished ? t("Setup complete") : t("Setup steps")}
          </span>
          <span className={styles.configureRailSub}>
            {profileFinished
              ? t("Your dispute profile is ready. Review or update any step any time.")
              : t("Complete each step to build the strongest response to your disputes.")}
          </span>
        </div>
        {/*  Step journey. Steps are always listed; once setup is finished a final
            "Review & activate" entry (the Summary step) appears below them.  */}
        <div className={styles.sectionGroup}>
          {(connectedProcessors?.length || 0) > 1 &&
          currentSection !== sectionKeys[0] &&
          currentSection !== "summary" ? (
            // The processor switcher itself lives at the top of the main content pane
            // (SectionForm), not here. This label is the rail's only remaining hint of
            // which processor the X/Y counts below belong to.
            <div className={styles.stepsFor}>
              <Text className={configureStyles.procListLabel}>
                {t("Steps for _name_", {
                  name: procTitle(activeProcessor || connectedProcessors[0])
                })}
              </Text>
            </div>
          ) : null}
          <div className={styles.stepsList}>
            {sectionKeys.map((k, i) => {
              // The Preview & Confirm step carries no fields: no X/Y count, and it is
              // "complete" only once every form step passes.
              const isPreview = k === "summary"
              const count = isPreview ? null : perSectionCount[k] || { answered: 0, total: 0 }
              // "Done" is the SAME bar that Next, Finish, the forward-jump lock and the
              // activation gate use (isStepComplete: every visible required field filled
              // and valid). It deliberately is NOT answered === total: optional fields
              // left blank do not block anything, so counting them here made a passable
              // step read "1/4" with no check while the rail let you walk straight past
              // it. One definition of complete, no contradiction between the badge and
              // the buttons.
              let complete = isPreview ? everythingComplete : isStepComplete(k, buildingBlocks)
              // A step whose every field is optional is passable from the start, so it
              // would read "Done" on a profile the merchant has not touched. Show it as
              // optional until it actually holds something.
              const optionalUntouched =
                !isPreview && isOptionalStep(k) && !optionalStepStarted(k, buildingBlocks)
              if (optionalUntouched) complete = false
              // Forward jumps can't skip an unfinished step: any step after the first
              // incomplete one is locked (going back to fix things stays open).
              const locked = blockedFrom != null && i > blockedFrom
              return (
                <SectionItem
                  key={k}
                  stepNumber={getSectionStep(k).stepNumber}
                  label={getSectionLabel(k)}
                  count={count}
                  optional={optionalUntouched}
                  active={k === currentSection}
                  complete={complete}
                  locked={locked}
                  lockReason={t('Complete "_step_" first.', {
                    step: t(getSectionLabel(firstIncomplete))
                  })}
                  onClick={() => goTo(k)}
                  t={t}
                />
              )
            })}
            {/*  Summary step: the review-and-activate destination, shown once setup is
                finished so it can't be reached before there's anything to review.
                Skipped when the step list already contains it (experimental Preview).  */}
            {profileFinished && !sectionKeys.includes("summary") ? (
              <button
                type="button"
                className={`${styles.sectionItem} ${styles.summaryItem} ${currentSection === "summary" ? styles.active : ""}`}
                onClick={() => goTo("summary")}
              >
                <span className={styles.stepBadge}>
                  <Icon type="checklist_bell" />
                </span>
                <span className={styles.sectionItemMeta}>
                  <span className={styles.sectionItemStep}>{t("Final")}</span>
                  <span className={styles.sectionItemLabel}>{t("Review & activate")}</span>
                </span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className={styles.configureRailAnchor}>
        <Tooltip
          title={
            canPreview
              ? ""
              : t("Complete Business Profile and Customer Experience to preview your evidence PDF.")
          }
        >
          <span className={styles.previewWrap} style={{ display: "block" }}>
            <Button
              type="primary"
              block
              disabled={!canPreview}
              onClick={onOpenPreview}
              text={t("Preview your evidence PDF")}
            />
          </span>
        </Tooltip>
      </div>
    </aside>
  )
}

export default LeftRail
